import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { useScopedSelect } from '@/contexts/ClientScopeContext';

/** Insights (cross-module) — mirrors server/insights/. */

export type Product = 'rmm' | 'cipp' | 'csp' | 'backup' | 'network' | 'domains' | 'docs' | 'soc';

export interface CompanyLinks {
  id: number;
  name: string;
  /** Autotask Classification tier (E / P / S); null = other classification, no box. */
  tier: 'E' | 'P' | 'S' | 'H' | 'L' | null;
  rmm: { sites: { uid: string; name: string; devices: number }[]; devices: number } | null;
  cipp: { customerId: string; displayName: string; defaultDomainName: string } | null;
  csp: { customers: string[]; subscriptions: number } | null;
  backup: { saasCustomerId: number; name: string; health: string; seats: number | null }[] | null;
  network: { firewalls: number; sites: number } | null;
  domains: { domains: string[] } | null;
  docs: { orgId: string; orgName: string; configurations: number } | null;
  soc: { orgs: { id: number; name: string }[]; agents: number; openIncidents: number } | null;
  products: Product[];
}

export interface UnmappedItem { product: Product; name: string; detail: string | null }

export interface CompaniesResponse {
  companies: CompanyLinks[];
  unmapped: UnmappedItem[];
  available: Record<Product, boolean>;
  productLabels: Record<Product, string>;
  builtAt: string;
}

export type CoverageState = 'in-sync' | 'drift' | 'rmm-only' | 'intune-only' | 'single';

export interface CoverageDevice {
  key: string;
  hostname: string;
  companyId: number | null;
  companyName: string | null;
  os: string | null;
  kind: 'workstation' | 'server' | 'mobile' | 'other';
  serial: string | null;
  rmm: { uid: string; hostname: string; siteName: string; online: boolean; lastSeen: string | null } | null;
  intune: { id: string; deviceName: string; lastSync: string | null; compliance: string | null; user: string | null; tenant: string } | null;
  match: { by: 'serial' | 'mac' | 'hostname'; confidence: 'high' | 'medium'; companyMismatch: boolean } | null;
  driftHours: number | null;
  state: CoverageState;
  flags: string[];
  /** ScreenConnect for the Datto device: yes / missing (gap) / n/a / unknown (still loading). null = no RMM side or not configured. */
  remote: { state: 'yes' | 'missing' | 'n/a' | 'unknown'; online: boolean | null } | null;
}

export interface CompanyCoverage {
  companyId: number;
  companyName: string;
  devices: number;
  inBoth: number;
  rmmOnly: number;
  intuneOnly: number;
  drift: number;
  pct: number | null;
  usesRmm: boolean;
  usesIntune: boolean;
}

export interface DeviceCoverageResponse {
  devices: CoverageDevice[];
  companies: CompanyCoverage[];
  driftHours: number;
  /** Records left out for not checking in (Admin → Insights; days 0 = off). */
  ignoredStale: { days: number; settingDays: number; rmm: number; intune: number };
  sources: { rmm: boolean; intune: boolean; screenconnect?: boolean; screenconnectReady?: boolean };
  fetchedAt: string;
}

export interface CompanyOrbit {
  company: CompanyLinks;
  /** IT Glue organisation linked to this company (null when none / not configured). */
  docs: { orgId: string; orgName: string; alert: string | null; quickNotes: string | null; url: string | null; configurations: number; notInRmm: number; expiringSoon: number } | null;
  network: {
    firewalls: { id: string; hostname: string; model: string | null; connected: boolean; subscriptionState: string; subscriptionEnd: string | null; openAlerts: number }[];
    sites: { id: string; label: string; hostConnected: boolean; devices: number; offline: number; internetIssues: number; pendingUpdates: number }[];
  } | null;
  domains: {
    items: { domain: string; stage: 'missing' | 'monitoring' | 'quarantine' | 'reject'; compliancePct: number | null; readyToEnforce: boolean; failingSenders: number }[];
    unmonitored: string[];
  } | null;
  available: Record<Product, boolean>;
  coverage: { summary: CompanyCoverage | null; devices: CoverageDevice[]; driftHours: number };
  tickets: {
    total: number | null;
    items: { id: number; ticketNumber: string; title: string; statusLabel: string; priorityLabel: string; createDate: string }[];
    error: string | null;
  };
  /** Health summary from every module's cache (server/insights/orbit.ts); each part null when unlinked/unavailable. */
  health: OrbitHealth;
}

export interface OrbitHealth {
  rmm: {
    devices: number; online: number; stale30: number; reboot: number; patchErrors: number; avIssues: number; patchPct: number | null; openAlerts: number;
    topAlerts: { alertUid: string; priority: string; summary: string; kind: string; timestamp: string; deviceUid: string | null; deviceName: string | null; ticketNumber: string | null }[];
  } | null;
  security: { building: true } | {
    building: false; customerId: string; secureScorePct: number | null; mfaRegisteredPct: number | null; mfaUnknown: boolean;
    unprotected: number | null; adminsNoMfa: number | null; enforcement: string | null; forwarding: number | null;
    issues: { severity: 'critical' | 'warning' | 'info'; label: string }[];
  } | null;
  backup: { saasCustomerId: number; name: string; health: string; backupPct: number | null; coveragePct: number | null; notProtected: number | null; leavers: number | null }[] | null;
  csp: { subscriptions: number; uploadedAt: string | null; renewals: { customer: string; plan: string; quantity: number; commitment: string; endDate: string; days: number; autoRenew: boolean }[] } | null;
  soc: { agents: number; silent: number; stale: number; defender: number; incidents90: number; open: { id: number; severity: string | null; summary: string; sentAt: string | null }[] } | null;
  gaps: { kind: GapKind; label: string; severity: GapSeverity; items: number; ticketed: number }[] | null;
  activity: { created30: number; completed30: number; items: { id: number; ticketNumber: string; title: string; statusLabel: string; at: string; event: 'opened' | 'completed' }[] } | null;
}

/** Built from other modules' server caches, so cheap — but not instant on a cold start. */
export function useCompanies(opts: { enabled?: boolean } = {}) {
  return useQuery({ queryKey: ['insights', 'companies'], queryFn: () => api.get<CompaniesResponse>('/api/insights/companies'), staleTime: 2 * 60_000, enabled: opts.enabled ?? true });
}

/** `includeStale` = the page's "Ignore stale devices" checkbox unticked. */
export function useDeviceCoverage(includeStale = false, opts: { scoped?: boolean } = {}) {
  const select = useScopedSelect<DeviceCoverageResponse>(opts.scoped, (d, inScope) => ({
    ...d, devices: d.devices.filter((x) => inScope(x.companyId)), companies: d.companies.filter((x) => inScope(x.companyId)),
  }));
  return useQuery({
    select,
    queryKey: ['insights', 'coverage', 'devices', includeStale],
    queryFn: () => api.get<DeviceCoverageResponse>(`/api/insights/coverage/devices${includeStale ? '?includeStale=1' : ''}`),
    staleTime: 2 * 60_000,
    placeholderData: (prev) => prev,
  });
}

export function useCompanyOrbit(id: number | undefined) {
  return useQuery({
    queryKey: ['insights', 'company', id],
    queryFn: () => api.get<CompanyOrbit>(`/api/insights/companies/${id}`),
    enabled: id != null && Number.isFinite(id),
    staleTime: 60_000,
  });
}

// ---------------- Gap list (server/insights/gaps.ts) ----------------

export type GapKind = 'no-rmm' | 'no-backup' | 'no-edr' | 'no-intune' | 'drift' | 'edr-silent' | 'not-billed' | 'no-remote' | 'remote-orphan' | 'domain-dmarcian' | 'domain-itglue' | 'domain-cipp' | 'leaver' | 'duplicate';
export type GapSeverity = 'critical' | 'warning' | 'advisory';

export interface GapTicket { id: number; ticketNumber: string; title: string | null; statusLabel: string | null; complete: boolean; linkedBy: string; linkedAt: string }
export interface GapItem { key: string; subject: string; detail: string; rmmUid: string | null; ticket: GapTicket | null }
export interface GapGroup {
  key: string;
  kind: GapKind;
  severity: GapSeverity;
  companyId: number | null;
  companyName: string | null;
  title: string;
  action: string;
  saasCustomerId: number | null;
  items: GapItem[];
}

export interface GapsResponse {
  groups: GapGroup[];
  driftHours: number;
  sources: { devices: boolean; intune: boolean; backup: boolean };
  kindLabels: Record<GapKind, string>;
  fetchedAt: string;
}

export interface OpenTicket { id: number; ticketNumber: string; title: string; statusLabel: string; priorityLabel: string; createDate: string; configurationItemID: number | null }

/** `scoped`: narrow to the client picked in the header (ClientScopeContext). */
export function useGaps(opts: { scoped?: boolean; enabled?: boolean } = {}) {
  const select = useScopedSelect<GapsResponse>(opts.scoped, (d, inScope) => ({ ...d, groups: d.groups.filter((x) => inScope(x.companyId)) }));
  return useQuery({ select, enabled: opts.enabled ?? true, queryKey: ['insights', 'gaps'], queryFn: () => api.get<GapsResponse>('/api/insights/gaps'), staleTime: 2 * 60_000 });
}

/** Open tickets a gap could be linked to (offered before "New ticket"). */
export function useGapTicketCandidates(companyId: number | null, enabled: boolean) {
  return useQuery({
    enabled: enabled && companyId != null,
    queryKey: ['insights', 'gaps', 'tickets', companyId],
    queryFn: () => api.get<{ total: number | null; items: OpenTicket[]; error: string | null }>(`/api/insights/gaps/tickets?companyId=${companyId}`),
    staleTime: 60_000,
  });
}

/** Link gap items to a ticket; `note` posts the evidence (built server-side) as an internal note. */
export function useLinkGaps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { keys: string[]; ticketId: number; note: boolean }) =>
      api.post<{ ticket: { id: number; ticketNumber: string; title: string }; noteError: string | null }>('/api/insights/gaps/link', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['insights', 'gaps'] }); },
  });
}

export function useUnlinkGaps() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (keys: string[]) => api.post<{ removed: number }>('/api/insights/gaps/unlink', { keys }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['insights', 'gaps'] }); },
  });
}

// ---------------- Record search + device / person 360 (server/insights/find.ts) ----------------

export type FindType = 'device' | 'person' | 'ticket' | 'company';
export interface FindResult {
  type: FindType;
  key: string;
  title: string;
  subtitle: string;
  companyId: number | null;
  companyName: string | null;
  matched: string;
  tags: string[];
  score: number;
}

/** Debounce in the caller; anything under 2 characters isn't sent. */
export function useFind(q: string) {
  const term = q.trim();
  return useQuery({
    queryKey: ['insights', 'find', term.toLowerCase()],
    queryFn: () => api.get<{ results: FindResult[]; partial: string[] }>(`/api/insights/find?q=${encodeURIComponent(term)}`),
    enabled: term.length >= 2,
    staleTime: 60_000,
    placeholderData: (prev) => prev,
  });
}

export interface DeviceOrbit {
  key: string;
  hostname: string;
  companyId: number | null;
  companyName: string | null;
  os: string | null;
  kind: CoverageDevice['kind'] | null;
  serial: string | null;
  state: CoverageState | null;
  flags: string[];
  match: CoverageDevice['match'];
  driftHours: number | null;
  driftLimit: number;
  staleDays: number;
  rmm: {
    uid: string; id: number; hostname: string; siteName: string; online: boolean; lastSeen: string | null; lastLoggedInUser: string | null;
    intIp: string | null; extIp: string | null; agentVersion: string | null; patchStatus: string | null; antivirusProduct: string | null; antivirusStatus: string | null;
    rebootRequired: boolean; lastReboot: string | null; warrantyDate: string | null; manufacturer: string | null; model: string | null;
    portalUrl: string; webRemoteUrl: string | null; stale: boolean;
  } | null;
  intune: {
    id: string; deviceName: string; lastSync: string | null; enrolled: string | null; compliance: string | null; ownership: string | null;
    user: string | null; userName: string | null; encrypted: boolean | null; autopilot: boolean; manufacturer: string | null; model: string | null; os: string;
    tenant: { customerId: string; displayName: string; defaultDomainName: string };
  } | null;
  itglue: { id: string; orgId: string; name: string; type: string | null; status: string | null; warrantyExpires: string | null; contact: string | null; location: string | null; url: string | null } | null;
  alerts: { alertUid: string; priority: string; timestamp: string; kind: string; summary: string; ticketNumber: string | null }[];
  ci: { id: number; name: string | null } | null;
  tickets: { id: number; ticketNumber: string; title: string; statusLabel: string; createDate: string }[];
  ticketsError: string | null;
  users: { key: string; label: string; via: string }[];
}

export function useDeviceOrbit(key: string | undefined) {
  return useQuery({
    queryKey: ['insights', 'device', key],
    queryFn: () => api.get<DeviceOrbit>(`/api/insights/devices/${encodeURIComponent(key!)}`),
    enabled: !!key,
    staleTime: 60_000,
  });
}

export interface PersonOrbit {
  key: string;
  name: string;
  emails: string[];
  companies: { id: number; name: string }[];
  contacts: { id: number; name: string; email: string | null; companyId: number; companyName: string; active: boolean; title: string | null; phone: string | null; mobile: string | null }[];
  /** The contact the live Microsoft 365 card is drawn for (its company has a mapped tenant). */
  cardContactId: number | null;
  m365: {
    id: string; upn: string; displayName: string; enabled: boolean | null; licensed: boolean | null; admin: boolean; guest: boolean;
    tenant: { customerId: string; displayName: string; defaultDomainName: string };
    companyId: number | null; companyName: string | null; tenantHealthy: boolean | null;
    mfa: { registered: boolean | null; methods: string[]; enforcement: string | null } | null;
    mailbox: { type: string | null; sizeGb: number | null; quotaGb: number | null; forwarding: string | null; litigationHold: boolean; archive: boolean } | null;
    backup: { state: string; saasCustomerId: number; seatState: string | null; exchange: string | null } | null;
  }[];
  devices: { key: string; hostname: string; via: string; online: boolean | null; lastSeen: string | null; os: string | null }[];
  tickets: { id: number; ticketNumber: string; title: string; statusLabel: string; createDate: string; companyName: string }[];
  ticketsError: string | null;
}

export function usePersonOrbit(key: string | undefined) {
  return useQuery({
    queryKey: ['insights', 'person', key],
    queryFn: () => api.get<PersonOrbit>(`/api/insights/people/${encodeURIComponent(key!)}`),
    enabled: !!key,
    staleTime: 60_000,
  });
}
